import { translate } from '@fiverr-private/i18n-react';
import { MetadataAnswersType } from '../../../../api/submitBrief';
import { T_FREELANCER_TYPE_PREFIX, T_TIMEZONE_PREFIX, T_LOCATION_PREFIX } from './consts';
import { CountriesAndRegions, FreelancerType, IdealCandidatePreferences } from './types';

export const getGMTOffset = (timeZone: string): string => {
    const now = new Date();

    const options = {
        timeZone,
        timeZoneName: 'short',
        hour12: false,
    } as const;

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);

    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value;
    return tzName || 'GMT';
};

export const getFreelancerTypeKeys = (freelancerType: FreelancerType) => ({
    ...freelancerType,
    isVettedPro: freelancerType?.isVettedPro ? 'prioritize_vetted_pro' : undefined,
});

export const getLanguageName = (code: string) => {
    try {
        return translate(`languages.codes.${code.toLocaleLowerCase()}`);
    } catch (error) {
        return undefined;
    }
};

export const getCountryName = (code: string) => {
    try {
        return translate(`countries.${code}`);
    } catch (error) {
        return undefined;
    }
};
export const getRegionName = (region: string) => translate(`${T_LOCATION_PREFIX}.regions.${region}`);

export const getCountriesAndRegionsText = (countriesAndRegions: CountriesAndRegions) => [
    ...countriesAndRegions.regions.map(getRegionName),
    ...countriesAndRegions.countries.map(getCountryName).filter((country): country is string => country !== undefined),
];

export const getTimezoneText = (timezoneId: string, maxOffsetHours: number) =>
    translate(`${T_TIMEZONE_PREFIX}.timezone_view`, {
        params: {
            timezone: getGMTOffset(timezoneId),
            hours: maxOffsetHours,
        },
    });

export const getFreelancerTypeText = (key: string) => translate(`${T_FREELANCER_TYPE_PREFIX}.${key}`);

export const snakeCaseFreelancerPreferences = ({
    languages,
    countries,
    regions,
    timezone,
    freelancerType,
}: IdealCandidatePreferences): MetadataAnswersType['freelancer_preferences'] => ({
    ...(languages?.length && {
        languages: languages.map((lang) => ({
            language_code: lang?.languageCode,
            min_proficiency: lang?.minProficiency,
        })),
    }),
    ...(countries?.length && { countries }),
    ...(regions?.length && { regions }),
    ...(timezone?.timezoneId && {
        timezone: {
            timezone_id: timezone.timezoneId,
            max_offset_hours: timezone.maxOffsetHours,
        },
    }),
    ...(freelancerType && {
        freelancer_type: {
            ...(freelancerType?.minLevel && { min_level: freelancerType.minLevel }),
            ...(freelancerType?.isVettedPro && { is_vetted_pro: freelancerType.isVettedPro }),
            ...(freelancerType?.agencyPreference && { agency_preference: freelancerType.agencyPreference }),
        },
    }),
});
